import * as THREE from 'three';
import { disposeO3D } from '../js/threejsHelper';
import { createSpriteText } from '@/utils/labeltext';
import { getCenter } from '@/utils/polygonHelper';

const ERASE_AREA_CHANNEL = '/businessResult/eraseObstacleAreas';
const AREA_COLOR = 0xffff00;
const ERASE_AREA_TYPE_TEXT = {
  0: '忽略未知',
  1: '忽略静态区域',
  2: '忽略建筑区域',
  3: '忽略车辆区域',
  4: '忽略闸机区域',
  5: '忽略路沿区域',
};

let model_group = null;
const areaFillMaterial = new THREE.MeshBasicMaterial({
  color: AREA_COLOR,
  opacity: 0.3,
  transparent: true,
  side: THREE.DoubleSide,
  depthWrite: false,
});
const areaEdgeMaterial = new THREE.LineBasicMaterial({
  color: AREA_COLOR,
});

/**
 * 被忽略区域：黄色半透明多边形，边框为同色实线
 */
export const load_erase_obstacle_areas = (vehicleData, threejs_globalScene) => {
  if (!localStorage.getItem('topicChannelChecks')?.includes(ERASE_AREA_CHANNEL)) {
    clearAreas();
    return;
  }
  if (!model_group) {
    model_group = new THREE.Group();
    model_group.position.set(0, 0, 0.05);
    threejs_globalScene.add(model_group);
  }
  clearAreas();
  const freeMoveCommand = vehicleData.businessResult?.remoteBusinessCommand?.remoteTakeoverCommand?.freeMoveCommand;
  const areas = (freeMoveCommand?.historyPreview?.environmentEdit || freeMoveCommand?.preview?.environmentEdit)?.eraseObstacleAreasList;
  mergeAreasByPointList(areas).forEach(({ points, types }) => {
    const polygon = createAreaPolygon(points, types);
    if (polygon) {
      model_group.add(polygon);
    }
  });
}

function mergeAreasByPointList(areas = []) {
  const groups = new Map();
  areas.forEach((area) => {
    const points = normalizePoints(area?.pointList);
    if (points.length < 3) return;
    const key = points.map((point) => `${point.xInt ?? point.x},${point.yInt ?? point.y}`).join('|');
    if (!groups.has(key)) {
      groups.set(key, { points, types: [] });
    }
    const group = groups.get(key);
    if (!group.types.includes(area.eraseAreaType)) {
      group.types.push(area.eraseAreaType);
    }
  });
  return [...groups.values()];
}

function createAreaPolygon(points, eraseAreaTypes) {
  if (!points || points.length < 3) {
    return null;
  }
  const shape = new THREE.Shape();
  points.forEach((p, index) => {
    if (index === 0) {
      shape.moveTo(p.x, p.y);
    } else {
      shape.lineTo(p.x, p.y);
    }
  });
  const fill = new THREE.Mesh(new THREE.ShapeGeometry(shape), areaFillMaterial);
  const edge = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(
      points.map((p) => new THREE.Vector3(p.x, p.y, 0.02)),
    ),
    areaEdgeMaterial,
  );
  const group = new THREE.Group();
  group.add(fill);
  group.add(edge);
  group.add(createTypeLabel(points, eraseAreaTypes));
  return group;
}

function createTypeLabel(points, eraseAreaTypes) {
  const center = getCenter(points);
  const typeTexts = (eraseAreaTypes || []).map((type) => ERASE_AREA_TYPE_TEXT[type] || `忽略${type}`);
  const label = createSpriteText(typeTexts, 48, '#ffffff', 120 * typeTexts.length, 420, null, 'normal');
  label.up = new THREE.Vector3(0, 0, 1);
  label.scale.set(5, 1.6 * typeTexts.length, 1);
  label.position.set(center?.x || 0, center?.y || 0, 0.3);
  label.name = 'erase_area_type_label';
  return label;
}

function normalizePoints(pointList) {
  if (!pointList?.length) {
    return [];
  }
  const points = pointList.filter((p) => p && (p.x || p.y));
  if (points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.x === last.x && first.y === last.y) {
      points.pop();
    }
  }
  return points;
}

function clearAreas() {
  if (!model_group) {
    return;
  }
  [...model_group.children].forEach((child) => {
    child.traverse?.((node) => {
      if (node.material && node.material !== areaFillMaterial && node.material !== areaEdgeMaterial) {
        node.material.map?.dispose();
        node.material.dispose();
      }
    });
    disposeO3D(child);
  });
}
